/**
 * Massa-berichten (`/mass-messages`) — wat er verstuurd is en hoe het afliep.
 *
 * 03-10-2026 (Jordy): de pagina heeft tabbladen. "Overview" is de pagina zoals
 * hij was (hieronder). "Create" plant tot drie Mass Messages tegelijk (zie
 * MassaCreate.tsx); die tab ziet alleen wie Mass DM mag. "Agenda" is een
 * kalender van wat er gepland staat (zie MassaAgenda.tsx; alleen owner, admin
 * en manager). Het tabblad staat in het adres (?tab=create, ?tab=agenda),
 * zodat herladen het tabblad houdt.
 *
 * Over het overzicht:
 *
 * Bewust ALLEEN lezen. Versturen gebeurt vanuit het chatvenster en vanuit de
 * bestaande flow; een knop "verstuur naar 2.525 fans" op een overzichtspagina
 * is één misklik van een ongeluk dat niet terug te draaien is. Wat hier wel
 * hoort: zien wat er is uitgegaan, hoeveel er is aangekomen, en waar het
 * misging.
 *
 * Eén endpoint: GET /api/admin/conversations/mass-message/history.
 *
 * 07-10-2026 (Jordy): geplande tijden in de tijdzone van de gebruiker, met
 * de zone erbij (Amsterdam zonder voorkeur), zodat er geen verwarring is.
 */
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { ErrorNote, Empty, Loading, Panel, Plat, Seg, Table, Tag } from '../components/ui'
import { useApi } from '../lib/useApi'
import { Modal } from '../components/Modal'
import { num, prijsVoorPlatform } from '../lib/format'
import { alsZone, geldigeZone, metZone } from '../lib/amsterdam'
import { useActions, mut } from '../lib/actions'
import { viaWorker } from '../lib/massDmWorker'
import { api, chatMediaUrl, thumbUrl } from '../lib/api'
import { magBetaaldeMassDm, useMij } from '../lib/rechten'
import { magMassaPagina, magMassDm } from '../lib/massaRechten'
import { MassaCreate } from './MassaCreate'
import { MassaAgenda } from './MassaAgenda'
import { ConceptBewerken, MassaConcepten, type Concept } from './MassaConcepten'
import { type AutoConcept, keuzesVoorConcept } from '../lib/mmAuto'
import { modelKeuzes } from '../components/MassDmFormulier'
import type { Persona } from './Models'
import { MassaAuto } from './MassaAuto'
import { MmModelFilter, modelOpties, platformVanSlug, type ModelOptie } from '../components/MmModelFilter'
import { EnRegel } from '../components/EnRegel'

interface Campagne {
  id: number
  created_at: string | null
  created_by: string | null
  platform: string
  model_slug: string | null
  model_name: string | null
  kind: string
  content: string | null
  /** 03-10-2026: het getypte Engels, als de fans een vertaling kregen. */
  original_content?: string | null
  has_media: boolean
  media: { url: string; type: string }[]
  is_paid: boolean
  price: number
  test_only: boolean
  recipient_count: number
  sent_count: number
  failed_count: number
  /** 23-10-2026: bezorgd. Bij een FanCentro Platform Mass DM = in de chats
   *  waargenomen kopieën (ondergrens), null zolang er nog geen zijn. */
  delivered?: number | null
  delivered_source?: 'platform' | 'observed_chat_copies'
  /** FanCentro Platform Mass DM: het ontvangersaantal is een schatting. */
  recipients_estimated?: boolean
  status: string
  job_id: string | null
  /** 01-10-2026: de server zegt of deze Platform Mass DM in te trekken is
   *  (F2F of FanCentro, geaccepteerd, platformnummer bekend, eigen campagne). */
  can_withdraw?: boolean
  platform_job_id?: string | null
  /** 22-10-2026: Promo Auto MM (bron=promo): welk model deze promo promoot. */
  promotes?: Promoot | null
  /** 24-10-2026: platform + platform-ID, voor het modelfilter. */
  model_key?: string | null
}

/** 22-10-2026 (Jordy): het gepromote model van een promo (Promo Auto MM). */
interface Promoot { target: string | null; kind: string; phase: number | null }

const TIP_WAARGENOMEN = 'Observed in the fans’ chats (lower bound). '
  + 'FanCentro does not report delivery for a Platform Mass DM.'
const TIP_SCHATTING = 'Estimate: FanCentro reports no recipient count; this is the follower count.'

/** Bezorgd zoals de server het meldt; oudere servers kennen alleen sent_count. */
function bezorgd(c: Campagne): number | null {
  return c.delivered !== undefined ? c.delivered : c.sent_count
}

function BezorgdCel({ c }: { c: Campagne }) {
  const n = bezorgd(c)
  const waargenomen = c.delivered_source === 'observed_chat_copies'
  return (
    <span title={waargenomen ? TIP_WAARGENOMEN : undefined}>
      {n == null ? '—' : num(n)}{waargenomen && n != null ? '*' : ''}
    </span>
  )
}

function OntvangersCel({ c }: { c: Campagne }) {
  return (
    <span title={c.recipients_estimated ? TIP_SCHATTING : undefined}>
      {c.recipients_estimated ? '≈' : ''}{num(c.recipient_count)}
    </span>
  )
}

function promootTekst(p: Promoot | null | undefined): string {
  if (!p?.target) return '—'
  return p.kind === 'self' ? `self-promo → ${p.target}` : `${p.target}${p.phase ? ` · day ${p.phase}/3` : ''}`
}

/**
 * 22-10-2026 (Jordy): het volledige bericht als voorbeeld bij hover (met het gepromote model). Vast in het
 * venster geplaatst, zodat de tabel niet verschuift en de rand van de tabel het niet afknipt. Alleen met
 * een muis; op een touchscherm opent een tik het bericht.
 */
function HoverVoorbeeld({ tekst, promoot }: { tekst: string | null; promoot?: Promoot | null }) {
  const [pos, setPos] = useState<{ x: number; y: number; boven: boolean } | null>(null)
  return (
    <div onMouseEnter={(e) => {
           if (!window.matchMedia('(hover: hover)').matches) return
           // De dashboardschaal (CSS-zoom op <html>, --dz): schermpixels terug naar CSS-pixels.
           const dz = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--dz')) || 1
           const r = e.currentTarget.getBoundingClientRect()
           const boven = r.bottom + 240 * dz > window.innerHeight
           setPos({ x: Math.max(8, Math.min(r.left, window.innerWidth - 436 * dz)) / dz,
                    y: (boven ? r.top - 6 : r.bottom + 6) / dz, boven })
         }}
         onMouseLeave={() => setPos(null)}>
      <div className="afkap">{tekst || <span className="dim">—</span>}</div>
      {/* In document.body: een ouder met een transform zou "fixed" anders verschuiven. */}
      {pos ? createPortal(
        <div className="hovervb" role="tooltip" data-testid="promo-hover"
             style={{ left: pos.x, top: pos.y, transform: pos.boven ? 'translateY(-100%)' : undefined }}>
          {promoot?.target ? <b>Promotes {promootTekst(promoot)}</b> : null}
          <span style={{ whiteSpace: 'pre-wrap' }}>{tekst || '(no text)'}</span>
        </div>, document.body) : null}
    </div>
  )
}

const PLATFORMS = [
  { value: 'all', label: 'All' },
  { value: 'fancentro', label: 'FanCentro' },
  { value: 'f2f', label: 'F2F' },
]

/**
 * Hoe de campagne is verstuurd (02-10-2026, Jordy): door het platform zelf
 * (`kind` bevat "native") of per fan door het dashboard. Dezelfde namen als
 * de methodekeuze in het Mass DM-venster, en dezelfde regel als de Live Feed.
 */
function methode(kind: unknown): 'Platform Mass DM' | 'Dashboard Mass Message' {
  return String(kind ?? '').toLowerCase().includes('native')
    ? 'Platform Mass DM'
    : 'Dashboard Mass Message'
}

/** De statuskleur zoals elders in het dashboard: groen af, amber bezig, rood mis. */
function StatusTag({ c }: { c: Campagne }) {
  const s = String(c.status || '').toLowerCase()
  if (s === 'done' && c.failed_count > 0) {
    return <Tag kind="warn">done · {num(c.failed_count)} failed</Tag>
  }
  if (s === 'done') return <Tag kind="ok">done</Tag>
  if (s === 'cancelled' || s === 'failed') return <Tag kind="bad">{s}</Tag>
  // 01-10-2026: ingetrokken via "Withdraw" (het platform haalt de kopieën weg).
  if (s === 'withdrawn') return <Tag kind="mut">withdrawn</Tag>
  return <Tag kind="warn">{s || 'unknown'}</Tag>
}

/** Eén geplande campagne, zoals `mass-message/scheduled` hem teruggeeft. */
interface Gepland {
  id: number
  platform: string
  model_slug: string | null
  model_name: string | null
  content: string | null
  scheduled_at: string | null
  status: string
  only_test: boolean
  audience_include?: string | null
  translate_to?: string | null
  error_message?: string | null
  has_media?: boolean
  media_count?: number
  is_paid?: boolean
  price?: number
  promotes?: Promoot | null
  model_key?: string | null
  // 21-10-2026: verzendmethode en filters (de server geeft ze al mee; Promo Auto MM bewerkt ze).
  fc_delivery_mode?: string | null
  exclude_recent_minutes?: number
  exclude_fan_active_minutes?: number
  exclude_timewasters?: boolean
  exclude_maybe_timewasters?: boolean
  exclude_spenders?: boolean
  exclude_new_followers?: boolean
  exclude_private_chats?: boolean
  online_only?: boolean
}

/** De tabbladen. 03-10-2026 (Jordy): 'agenda' alleen voor owner/admin/manager.
 *  04-10-2026 (Jordy): 'drafts' (concepten) voor wie Create ziet; goedkeuren
 *  alleen owner/admin (zie MassaConcepten.tsx). */
const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'create', label: 'Create' },
  { value: 'agenda', label: 'Agenda' },
  { value: 'drafts', label: 'Drafts' },
  // 11-10-2026 (Jordy): automatische Mass Messages nalopen (zie MassaAuto.tsx).
  { value: 'auto', label: 'Auto' },
]

export default function MassMessages() {
  const mij = useMij()
  const [zoek, setZoek] = useSearchParams()
  if (mij.loading && !mij.data) return <Loading />
  // 05-10-2026 (Jordy): de hele pagina (Overview, Create, Drafts, Agenda) is
  // voor owner en admin, en voor een manager met de schuif "Mass Messages
  // page". 07-10-2026: ook een chatter met die schuif (de server toont hem
  // alleen zijn eigen modellen). Zonder schuif niet, ook niet via de directe
  // URL (de server geeft 403).
  const pagina = magMassaPagina(mij.data)
  if (!pagina) {
    return (
      <Empty>
        <span data-testid="massa-geen-toegang">
          The Mass Messages page isn't enabled for your account. Ask an owner or admin to turn it on.
        </span>
      </Empty>
    )
  }
  const magOverzicht = pagina
  const magCreate = pagina && magMassDm(mij.data)
  const magAgenda = pagina
  const tabs = TABS.filter((t) => (
    t.value === 'overview' ? magOverzicht
      : t.value === 'create' || t.value === 'drafts' ? magCreate : t.value === 'auto' ? magCreate : magAgenda))
  const tab = tabs.some((t) => t.value === zoek.get('tab'))
    ? (zoek.get('tab') as string)
    : (tabs[0]?.value ?? 'overview')
  const setTab = (v: string) => setZoek(v === 'overview' ? {} : { tab: v }, { replace: true })
  return (
    <>
      {tabs.length > 1 ? (
        <div className="filters">
          <Seg options={tabs} value={tab} onChange={setTab} />
        </div>
      ) : null}
      {tab === 'create' && magCreate ? (
        <MassaCreate
          magDirect={magMassDm(mij.data, 'direct')}
          magNative={magMassDm(mij.data, 'native')}
          magBetaald={magBetaaldeMassDm(mij.data)}
          magOverzicht={magOverzicht}
        />
      ) : tab === 'agenda' && magAgenda ? <MassaAgenda />
        : tab === 'auto' && magCreate ? <MassaAuto />
        : tab === 'drafts' && magCreate ? (
          <MassaConcepten
            magDirect={magMassDm(mij.data, 'direct')}
            magNative={magMassDm(mij.data, 'native')}
            magBetaald={magBetaaldeMassDm(mij.data)}
            magGoedkeuren={magMassaPagina(mij.data)}
          />
        ) : magOverzicht ? <Overzicht zone={geldigeZone(mij.data?.timezone)} /> : null}
    </>
  )
}

/** Bij een geplande promo-rij (Promo Auto MM) opent Edit het promo-planvenster. */
export type GeplandRij = Gepland

/** De tab Overview: precies de pagina van vóór 03-10-2026.
 *  21-10-2026 (Jordy): ook voor Promo Auto MM (`bron="promo"`): zelfde weergave, alleen promo-rijen. */
export function Overzicht({ zone, bron, onBewerk }: {
  zone: string
  bron?: 'promo'
  /** Promo: Edit opent het eigen planvenster (tijd + opties) in plaats van het MM-bewerkvenster. */
  onBewerk?: (g: Gepland, ververs: () => void) => void
}) {
  // Vijftig is ruim een maand campagnes. Alles tegelijk maakte de pagina meer
  // dan tienduizend pixels lang en dan is de bovenste rij niet meer te vinden.
  const [limiet, setLimiet] = useState(50)
  const basis = `/api/admin/conversations/mass-message/history?limit=${limiet}${bron ? `&bron=${bron}` : ''}`
  const q = useApi<Campagne[]>(basis, [limiet])
  // 24-10-2026 (Jordy): weergave, platform en modellen staan in het adres (?w=, ?pf=, ?m=), zodat
  // Refresh, wisselen tussen Sent en Scheduled en herladen ze niet wissen.
  const [zoek, setZoek] = useSearchParams()
  const zet = (sleutel: string, waarde: string) => setZoek((oud) => {
    const n = new URLSearchParams(oud)
    if (waarde) n.set(sleutel, waarde)
    else n.delete(sleutel)
    return n
  }, { replace: true })
  const platform = PLATFORMS.some((p) => p.value === zoek.get('pf')) ? (zoek.get('pf') as string) : 'all'
  const setPlatform = (v: string) => zet('pf', v === 'all' ? '' : v)
  const weergave: 'sent' | 'scheduled' = zoek.get('w') === 'scheduled' ? 'scheduled' : 'sent'
  const setWeergave = (v: 'sent' | 'scheduled') => zet('w', v === 'sent' ? '' : v)
  const gekozen = (zoek.get('m') ?? '').split(',').filter(Boolean)
  // Met een platform alleen de modellen van dat platform (een keuze van het andere platform telt niet mee).
  const effectief = gekozen.filter((s) => platform === 'all' || !platformVanSlug(s) || platformVanSlug(s) === platform)
  const filterActief = platform !== 'all' || effectief.length > 0
  // Sent: op de server gefilterd met dezelfde limiet (de lijst is niet volledig geladen: de laatste
  // 50 campagnes zijn vaak minder dan een dag); de ongefilterde lijst geeft de keuzes en het totaal.
  const qf = useApi<Campagne[]>(filterActief
    ? `${basis}${platform !== 'all' ? `&platform=${platform}` : ''}${effectief.length ? `&model_slugs=${encodeURIComponent(effectief.join(','))}` : ''}`
    : null, [limiet, platform, effectief.join(',')])
  const [geplandOpties, setGeplandOpties] = useState<ModelOptie[]>([])
  const opties = weergave === 'scheduled' ? geplandOpties : modelOpties(q.data ?? [], platform)
  const [open, setOpen] = useState<Campagne | null>(null)
  // 18-09-2026 (Jordy, HERSTEL-008): wat vooruit gepland staat hoorde hier
  // zichtbaar te zijn — anders plan je iets en zie je het nooit meer terug.
  // 03-10-2026: alleen wat hier getoond wordt (gepland en overgeslagen). De
  // server geeft er hooguit 200, oudste eerst; zonder dit filter vielen nieuwe
  // planningen weg zodra er 200 oude (verstuurde) rijen waren.
  const { run, busy } = useActions()

  const rijen = useMemo(() => (filterActief ? (qf.data ?? []) : (q.data ?? [])), [filterActief, qf.data, q.data])
  const laadt = filterActief ? qf.loading && !qf.data : q.loading
  const lijstFout = filterActief ? qf.error : q.error

  const totalen = useMemo(() => rijen.reduce((a, c) => ({
    campagnes: a.campagnes + 1,
    ontvangers: a.ontvangers + (c.recipient_count || 0),
    verstuurd: a.verstuurd + (bezorgd(c) || 0),
    mislukt: a.mislukt + (c.failed_count || 0),
  }), { campagnes: 0, ontvangers: 0, verstuurd: 0, mislukt: 0 }), [rijen])

  return (
    <>
      <div className="filters">
        <Seg options={[{ value: 'sent', label: !q.data ? 'Sent'
                         : filterActief ? `Sent (${laadt ? '…' : rijen.length} of ${q.data.length})` : `Sent (${q.data.length})` },
                       { value: 'scheduled', label: 'Scheduled' }]}
             value={weergave} onChange={(v) => setWeergave(v as 'sent' | 'scheduled')} />
        <Seg options={PLATFORMS} value={platform} onChange={setPlatform} platform />
        <MmModelFilter opties={opties} gekozen={effectief} onChange={(slugs) => zet('m', slugs.join(','))} />
        <span className="spacer" style={{ flex: 1 }} />
        {weergave === 'scheduled' ? null : <span className="mut2" style={{ fontSize: 12 }}>
          {num(totalen.campagnes)} campaigns · {num(totalen.verstuurd)} sent ·{' '}
          {totalen.mislukt
            ? <span style={{ color: 'var(--bad)' }}>{num(totalen.mislukt)} failed</span>
            : '0 failed'}
        </span>}
      </div>

      {weergave === 'scheduled' ? (
        <GeplandLijst zone={zone} platform={platform} bron={bron} onBewerk={onBewerk} modellen={effectief}
                      onOpties={setGeplandOpties} />
      ) : <>

      {/* Deze pagina verstuurt niets. Dat hoort er te staan, want een
          overzicht van massa-berichten wekt de indruk dat je er ook mee kunt
          versturen. */}
      <div className="note" style={{ marginBottom: 16 }}>
        {bron === 'promo'
          ? 'Read-only. Promo Mass Messages go out from Review (Send approved or Schedule); this shows what went out and how it landed.'
          : 'Read-only. Sending happens from the chat window; this page shows what went out and how it landed.'}
      </div>


      {laadt ? <Loading /> : lijstFout ? <ErrorNote error={lijstFout} /> : (
        rijen.length === 0 ? <Empty>{filterActief ? 'No campaigns for this filter.' : 'No campaigns yet.'}</Empty> : (
          <Table
            cols={[
              { key: 'when', label: 'Sent' },
              { key: 'model', label: 'Model' },
              // 22-10-2026 (Jordy): bij Promo Auto MM ook welk model de promo promoot.
              ...(bron === 'promo' ? [{ key: 'promo', label: 'Promotes' }] : []),
              { key: 'by', label: 'By' },
              { key: 'method', label: 'Method' },
              { key: 'msg', label: 'Message' },
              { key: 'rcpt', label: 'Recipients', num: true },
              { key: 'sent', label: 'Delivered', num: true },
              { key: 'fail', label: 'Failed', num: true },
              { key: 'status', label: 'Status' },
            ]}
          >
            {rijen.map((c) => (
              <tr key={c.id} className="klikrij" onClick={() => setOpen(c)}>
                <td className="mut mono">{alsZone(c.created_at, zone)}</td>
                <td>
                  <Plat value={c.platform} />{' '}
                  {c.model_name || c.model_slug || '—'}
                </td>
                {bron === 'promo' ? <td data-testid="promo-promotes">{promootTekst(c.promotes)}</td> : null}
                <td className="mut">{c.created_by || '—'}</td>
                {/* 02-10-2026 (Jordy): Platform Mass DM of Dashboard Mass Message. */}
                <td><Tag kind="mut">{methode(c.kind)}</Tag></td>
                <td style={{ maxWidth: 380 }}>
                  {bron === 'promo'
                    ? <HoverVoorbeeld tekst={c.content} promoot={c.promotes} />
                    : <div className="afkap">{c.content || <span className="dim">—</span>}</div>}
                  {c.has_media ? <Tag kind="mut">media</Tag> : null}
                  {/* 02-10-2026 (Jordy): FanCentro in credits, F2F in euro's. */}
                  {c.is_paid ? <Tag kind="acc">{prijsVoorPlatform(c.price, c.platform)}</Tag> : null}
                  {c.test_only ? <Tag kind="warn">test</Tag> : null}
                </td>
                <td className="num mono"><OntvangersCel c={c} /></td>
                <td className="num mono"><BezorgdCel c={c} /></td>
                <td className="num mono"
                    style={c.failed_count ? { color: 'var(--bad)' } : undefined}>
                  {num(c.failed_count)}
                </td>
                <td><StatusTag c={c} /></td>
              </tr>
            ))}
          </Table>
        )
      )}

      {!laadt && rijen.length >= limiet ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 14 }}>
          <button className="btn ghost sm" type="button"
                  onClick={() => setLimiet((n) => n + 100)}>
            Load more
          </button>
        </div>
      ) : null}
      </>}

      {open ? (
        <Modal
          title={`Campaign ${open.id}`}
          desc={`${open.model_name || open.model_slug} · ${open.platform}`}
          width={620}
          onClose={() => setOpen(null)}
          footer={
            <>
              <button className="btn ghost sm" type="button"
                      onClick={() => setOpen(null)}>Close</button>
              {/* 01-10-2026 (Jordy): een verstuurde Platform Mass DM intrekken,
                  op F2F en FanCentro. Gaat via de worker; het platform haalt de
                  kopieën bij de fans daarna zelf weg (met wat vertraging). */}
              {open.can_withdraw ? (
                <button className="btn sm" type="button" style={{ color: 'var(--bad)' }}
                        disabled={busy === 'Withdraw Mass DM'}
                        onClick={() => run({
                          label: 'Withdraw Mass DM',
                          confirm: `Withdraw this Mass DM on ${open.platform === 'f2f' ? 'F2F' : 'FanCentro'}? `
                            + 'The platform removes it from every fan’s chat. Fans who already opened it have seen it.',
                          request: () => viaWorker(
                            `/api/admin/conversations/mass-message/withdraw/${open.id}`, {}),
                          onDone: () => { setOpen(null); q.refetch() },
                          successText: 'Mass DM withdrawn — the platform is removing the copies.',
                        })}>
                  Withdraw
                </button>
              ) : null}
            </>
          }
        >
          <div className="kv" style={{ marginBottom: 12 }}>
            <span>Sent</span><span className="mono">{metZone(open.created_at, zone)}</span>
            <span>Method</span><span>{methode(open.kind)}</span>
            {bron === 'promo' ? <><span>Promotes</span><span>{promootTekst(open.promotes)}</span></> : null}
            <span>By</span><span>{open.created_by || '—'}</span>
            <span>Recipients</span><span className="mono"><OntvangersCel c={open} /></span>
            <span>Delivered</span><span className="mono"><BezorgdCel c={open} /></span>
            {open.delivered_source === 'observed_chat_copies' || open.recipients_estimated ? (
              <>
                <span />
                <span className="mut2" style={{ fontSize: 11.5 }}>
                  {open.recipients_estimated ? '≈ estimate (follower count). ' : ''}
                  {open.delivered_source === 'observed_chat_copies'
                    ? '* observed in chats, a lower bound; FanCentro does not confirm delivery.' : ''}
                </span>
              </>
            ) : null}
            <span>Failed</span>
            <span className="mono" style={open.failed_count ? { color: 'var(--bad)' } : undefined}>
              {num(open.failed_count)}
            </span>
            <span>Status</span><span>{open.status}</span>
            {open.is_paid ? (
              <>
                <span>Price</span>
                <span className="mono">{prijsVoorPlatform(open.price, open.platform)}</span>
              </>
            ) : null}
            <span>Job</span><span className="mono">{open.job_id || '—'}</span>
          </div>
          {/* 03-10-2026 (Jordy): bij een vertaalde Mass DM wat de fans kregen
              én het Engels zoals het getypt is. */}
          <b style={{ fontSize: 12.5 }}>{open.original_content ? 'Fans received' : 'Message'}</b>
          <div className="note" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
            {open.content || '(no text)'}
          </div>
          {open.original_content ? (
            <>
              <b style={{ fontSize: 12.5, display: 'block', marginTop: 10 }}>Typed (English)</b>
              <div className="note" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
                {open.original_content}
              </div>
            </>
          ) : null}
          {open.has_media ? <VerstuurdeContent media={open.media ?? []} /> : null}
          {open.failed_count ? (
            <div className="note" style={{ marginTop: 12 }}>
              {num(open.failed_count)} of {num(open.recipient_count)} did not
              arrive. Meestal een fan die niet meer volgt of een chat die dicht
              is; de worker bewaart per ontvanger wat er misging.
            </div>
          ) : null}
        </Modal>
      ) : null}
    </>
  )
}

/**
 * 02-10-2026 (Jordy): de content die met deze Mass Message is verstuurd.
 * Foto's en video's uit de bibliotheek staan in `media_urls` als
 * `/api/admin/content/file/<id>`; een video toont eerst de miniatuur en speelt
 * na een klik. Een eigen upload (zonder bibliotheek-id) toont het bestand zelf.
 */
function VerstuurdeContent({ media }: { media: { url: string; type: string }[] }) {
  const [speelt, setSpeelt] = useState<number | null>(null)
  if (!media.length) {
    return (
      <>
        <b style={{ fontSize: 12.5, display: 'block', marginTop: 12 }}>Content</b>
        <div className="note" style={{ marginTop: 6 }}>Media was sent, but no preview was saved for this campaign.</div>
      </>
    )
  }
  return (
    <>
      <b style={{ fontSize: 12.5, display: 'block', marginTop: 12 }}>Content · {media.length}</b>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
        {media.map((m, i) => {
          const id = Number((/\/api\/admin\/content\/file\/(\d+)/.exec(m.url) ?? [])[1])
          const bron = chatMediaUrl(m.url) ?? m.url
          const video = String(m.type).toLowerCase() === 'video'
          const stijl = { width: 132, height: 132, objectFit: 'cover' as const, borderRadius: 8, background: 'var(--bg2)' }
          if (video && speelt === i) {
            return <video key={i} src={bron} controls autoPlay style={{ ...stijl, width: 240, height: 240 }} />
          }
          return (
            <button key={i} type="button" className="massa-content" title={video ? 'Play video' : 'Open photo'}
                    style={{ position: 'relative', padding: 0, border: 0, background: 'none', cursor: 'pointer' }}
                    onClick={() => (video ? setSpeelt(i) : window.open(bron, '_blank', 'noopener'))}>
              {Number.isFinite(id) && id > 0
                ? <img src={thumbUrl(id)} alt="" loading="lazy" style={stijl} />
                : video ? <video src={bron} preload="metadata" muted style={stijl} />
                  : <img src={bron} alt="" loading="lazy" style={stijl} />}
              {video ? <span style={{ position: 'absolute', left: 6, bottom: 6 }}><Tag kind="mut">▶ video</Tag></span> : null}
            </button>
          )
        })}
      </div>
    </>
  )
}


interface PauzeStand { paused: boolean; since: string | null; by: string | null; past_due: number; can_change: boolean }

/** Minder dan zoveel voor de verzendtijd is een geplande rij vergrendeld (server: 3 min). */
const VERGRENDEL_MS = 3 * 60_000

/**
 * 16-10-2026 (Jordy): de weergave "Scheduled" van de Overview. Alles met status
 * 'scheduled', komende eerst, met preview, doelgroep en PPV. Aanpassen (tekst,
 * Make PPV, tijd) en annuleren zolang de verzender de rij nog niet oppakt; vlak
 * voor de verzendtijd is de rij vergrendeld (de server weigert het dan ook).
 */
function GeplandLijst({ zone, platform, bron, onBewerk, modellen = [], onOpties }: {
  zone: string; platform: string; bron?: 'promo'; onBewerk?: (g: Gepland, ververs: () => void) => void
  /** 24-10-2026: gekozen model-slugs (leeg = alle); de lijst is volledig geladen, dus hier gefilterd. */
  modellen?: string[]
  /** De modelkeuzes uit deze lijst (voor het filter in de balk). */
  onOpties?: (o: ModelOptie[]) => void
}) {
  const van = useMemo(() => new Date(Date.now() - 7 * 86_400_000).toISOString(), [])
  const q = useApi<Gepland[] | { items: Gepland[] }>(
    `/api/admin/conversations/mass-message/scheduled?status=scheduled,skipped&limit=2000&from=${encodeURIComponent(van)}${bron ? `&bron=${bron}` : ''}`)
  const { run, busy } = useActions()
  const [bewerk, setBewerk] = useState<number | null>(null)
  const [lees, setLees] = useState<Gepland | null>(null)
  const [nu, setNu] = useState(() => Date.now())
  // 18-10-2026 (Jordy): Pause/Play voor het versturen van ALLE geplande MM's van dit merk.
  const pauze = useApi<PauzeStand>('/api/admin/mass-auto/pause')
  const pauzeer = () => run({
    label: 'Pause sending',
    confirm: 'Pause sending of ALL scheduled Mass Messages for this brand? Nothing is sent until you press Play. '
      + 'Generating and approving continue.',
    request: () => api.post('/api/admin/mass-auto/pause', {}),
    successText: 'Sending paused.',
    onDone: () => { pauze.refetch(); q.refetch() },
  })
  const hervat = async () => {
    let n = 0
    try {
      n = (await api.get<PauzeStand>('/api/admin/mass-auto/pause')).past_due
    } catch {
      pauze.refetch()
      return
    }
    await run({
      label: 'Resume sending',
      confirm: n
        ? `${n} scheduled Mass Message(s) whose time has passed will be DELETED (a backup is kept). `
          + 'Future ones stay and go out at their time. Resume sending?'
        : 'No scheduled Mass Messages have passed their time. Future ones go out at their time. Resume sending?',
      request: () => api.post('/api/admin/mass-auto/resume', { expected_count: n }),
      successText: n ? `Sending resumed; ${n} past message(s) deleted.` : 'Sending resumed.',
      onDone: () => { pauze.refetch(); setNu(Date.now()); q.refetch() },
    })
  }
  // 19-10-2026 (Jordy): "Cancel all scheduled" voor dit merk (eigen database).
  const annuleerAlles = async () => {
    let n = 0
    try {
      n = (await api.get<{ count: number }>('/api/admin/mass-auto/cancel-all-scheduled')).count
    } catch {
      q.refetch()
      return
    }
    if (!n) {
      window.alert('There are no scheduled Mass Messages to cancel.')
      q.refetch()
      return
    }
    await run({
      label: 'Cancel all scheduled',
      confirm: `Cancel ${n} scheduled Mass Message(s)? This is every scheduled message of this brand `
        + '(all models, FC and F2F, whatever filter is shown). A backup is kept. '
        + 'Messages already sent or being sent are not touched.',
      request: () => api.post('/api/admin/mass-auto/cancel-all-scheduled', { expected_count: n }),
      successText: `${n} scheduled Mass Message(s) cancelled.`,
      onDone: () => { pauze.refetch(); setNu(Date.now()); q.refetch() },
    })
  }
  // Stabiel per antwoord van de server: anders gaven de modelkeuzes bij elke render een nieuwe lijst door.
  const alle = useMemo(() => (Array.isArray(q.data) ? q.data : (q.data?.items ?? [])), [q.data])
  const opties = useMemo(() => modelOpties(alle, platform), [alle, platform])
  useEffect(() => { onOpties?.(opties) }, [opties, onOpties])
  const modelSet = new Set(modellen)
  const passend = alle.filter((g) => (platform === 'all' || String(g.platform).toLowerCase() === platform)
    && (!modelSet.size || modelSet.has(g.model_slug || '')))
  const open_ = passend.filter((g) => String(g.status || '') === 'scheduled')
    .sort((a, b) => Date.parse(a.scheduled_at || '') - Date.parse(b.scheduled_at || ''))
  const totaal = alle.filter((g) => String(g.status || '') === 'scheduled').length
  const gefilterd = platform !== 'all' || modelSet.size > 0
  const overgeslagen = passend.filter((g) => String(g.status || '') === 'skipped')
  if (q.loading && !q.data) return <Loading />
  if (q.error) return <ErrorNote error={q.error} />
  // 21-10-2026: op de promo-pagina geen Pause/Cancel all (die gelden voor ALLE MM's van het merk).
  const p = bron ? null : pauze.data
  return (
    <>
      {p ? (
        <div className="card row" data-testid="mm-pauze"
             style={{ marginBottom: 12, gap: 12, alignItems: 'center', flexWrap: 'wrap',
                      borderLeft: `3px solid ${p.paused ? 'var(--bad, #ef4444)' : 'var(--ok, #3ddc97)'}` }}>
          <b>{p.paused ? 'Sending is paused' : 'Sending is running'}</b>
          <span className="mut2" style={{ fontSize: 12 }}>
            {p.paused
              ? `Paused since ${alsZone(p.since, zone)} (${zone})${p.by ? ` by ${p.by}` : ''}. Nothing is sent; generating and approving continue.`
              : 'Scheduled Mass Messages go out at their time. Pause stops all sending for this brand (FC and F2F).'}
          </span>
          <span style={{ flex: 1 }} />
          {p.can_change ? (p.paused
            ? <button className="btn primary sm" type="button" disabled={busy !== null}
                      onClick={() => { void hervat() }}>▶ Play</button>
            : <button className="btn sm" type="button" disabled={busy !== null}
                      onClick={() => { void pauzeer() }}>⏸ Pause</button>) : null}
          {p.can_change ? (
            <button className="btn danger sm" type="button" data-testid="mm-cancel-all"
                    disabled={busy !== null}
                    onClick={() => { void annuleerAlles() }}>Cancel all scheduled</button>) : null}
        </div>
      ) : null}
      <Panel title={gefilterd ? `Scheduled (${open_.length} of ${totaal})` : `Scheduled (${open_.length})`} flush
             desc={<>Upcoming first. Times in {zone}. Edit or cancel until {VERGRENDEL_MS / 60_000} minutes before sending; then the row is locked so a change can't clash with sending.</>}
             right={<button className="btn ghost sm" type="button" onClick={() => { setNu(Date.now()); q.refetch() }}>Refresh</button>}>
        <Table cols={[
          { key: 'w', label: 'When' }, { key: 'm', label: 'Model' },
          ...(bron === 'promo' ? [{ key: 'd', label: 'Promotes' }] : []), { key: 'a', label: 'Audience' },
          { key: 't', label: 'Message' }, { key: 'p', label: 'PPV' }, { key: 'x', label: '' },
        ]} emptyText="Nothing scheduled.">
          {open_.map((g) => {
            const dicht = Date.parse(g.scheduled_at || '') - nu <= VERGRENDEL_MS
            return (
              <tr key={g.id} data-testid="gepland-rij">
                <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                  {alsZone(g.scheduled_at, zone)} <span className="mut2" style={{ fontSize: 11 }}>{zone}</span>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}><Plat value={g.platform} /> {g.model_name || g.model_slug || '—'}</td>
                {bron === 'promo' ? <td data-testid="promo-promotes">{promootTekst(g.promotes)}</td> : null}
                <td className="mut2" style={{ fontSize: 12 }}>
                  {g.only_test ? 'Test accounts' : (g.audience_include || 'all')}
                  {g.translate_to ? ` · → ${g.translate_to}` : ''}
                </td>
                {bron === 'promo' ? (
                  // Hover toont het hele bericht; een tik (mobiel) opent het.
                  <td style={{ maxWidth: 380, cursor: 'pointer' }} onClick={() => setLees(g)} data-testid="promo-bericht">
                    <HoverVoorbeeld tekst={g.content} promoot={g.promotes} />
                  </td>
                ) : (
                  <td style={{ maxWidth: 380 }} title={g.content || ''}>
                    <div className="afkap">{g.content || <span className="dim">—</span>}</div>
                    <EnRegel tekst={g.content} afkappen />
                  </td>
                )}
                <td style={{ whiteSpace: 'nowrap' }}>
                  {g.is_paid ? <Tag kind="warn">PPV {prijsVoorPlatform(g.price ?? 0, g.platform)}</Tag> : null}
                  {g.has_media ? <> <Tag kind="mut">media{g.media_count ? ` ×${g.media_count}` : ''}</Tag></> : null}
                  {!g.is_paid && !g.has_media ? <span className="dim">text</span> : null}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn ghost sm" type="button" onClick={() => setLees(g)}
                          title="Read the full message">Open</button>{' '}
                  {dicht ? <Tag kind="mut" title="Being sent or about to be sent">locked</Tag> : (
                    <>
                      <button className="btn ghost sm" type="button" disabled={busy !== null}
                              onClick={() => (onBewerk ? onBewerk(g, () => { setNu(Date.now()); q.refetch() }) : setBewerk(g.id))}>Edit</button>{' '}
                      <button className="btn ghost sm" type="button" disabled={busy !== null}
                              onClick={() => run({
                                label: 'Cancel scheduled mass DM',
                                confirm: `Cancel this scheduled Mass Message (${alsZone(g.scheduled_at, zone)})? It will not be sent.`,
                                request: mut.post(`/api/admin/conversations/mass-message/scheduled/${g.id}/cancel`),
                                onDone: q.refetch,
                                successText: 'Scheduled mass DM cancelled.',
                              })}>Cancel</button>
                    </>
                  )}
                </td>
              </tr>
            )
          })}
          {overgeslagen.map((g) => (
            <tr key={`s${g.id}`} title={g.error_message || undefined}>
              <td className="mono">{alsZone(g.scheduled_at, zone)}</td>
              <td><Plat value={g.platform} /> {g.model_name || g.model_slug || '—'}</td>
              {bron === 'promo' ? <td>{promootTekst(g.promotes)}</td> : null}
              <td className="mut2" style={{ fontSize: 12 }}>{g.only_test ? 'Test accounts' : (g.audience_include || 'all')}</td>
              <td className="mut" style={{ maxWidth: 380 }}><div className="afkap">{g.content || '—'}</div></td>
              <td />
              <td><Tag kind="bad">skipped · not sent</Tag></td>
            </tr>
          ))}
        </Table>
      </Panel>
      {lees ? (
        <Modal title={`Scheduled Mass Message · ${lees.model_name || lees.model_slug || ''}`} width={560}
               onClose={() => setLees(null)}>
          <div className="col" style={{ gap: 10 }} data-testid="gepland-lees">
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Plat value={lees.platform} />
              <span className="mono">{alsZone(lees.scheduled_at, zone)}</span> <span className="mut2">{zone}</span>
              {lees.is_paid ? <Tag kind="warn">PPV {prijsVoorPlatform(lees.price ?? 0, lees.platform)}</Tag> : null}
              {lees.has_media ? <Tag kind="mut">media{lees.media_count ? ` ×${lees.media_count}` : ''}</Tag> : null}
            </div>
            {bron === 'promo' ? (
              <div style={{ fontSize: 13 }} data-testid="gepland-promotes">Promotes <b>{promootTekst(lees.promotes)}</b></div>
            ) : null}
            <div className="mut2" style={{ fontSize: 12 }}>
              Audience: {lees.only_test ? 'Test accounts' : (lees.audience_include || 'all')}
              {lees.translate_to ? ` · translated to ${lees.translate_to}` : ''}
            </div>
            <div className="card" style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5 }}>
              {lees.content || <span className="dim">(no text)</span>}
            </div>
            <EnRegel tekst={lees.content} />
          </div>
        </Modal>
      ) : null}
      {bewerk != null ? (
        <GeplandBewerken id={bewerk} zone={zone} onSluit={() => setBewerk(null)}
                         onOpgeslagen={() => { setBewerk(null); setNu(Date.now()); q.refetch() }} />
      ) : null}
    </>
  )
}

/** Haalt het geplande bericht op in het formaat van het bewerkvenster en opent het (modus gepland). */
function GeplandBewerken({ id, zone, onSluit, onOpgeslagen }: {
  id: number; zone: string; onSluit: () => void; onOpgeslagen: () => void
}) {
  const mij = useMij()
  const info = useApi<{ item: Concept; locked: boolean; lock_reason: string | null; auto: boolean }>(
    `/api/admin/conversations/mass-message/scheduled/${id}/edit`)
  const personas = useApi<Persona[]>('/api/admin/models/personas')
  const c = info.data?.item
  const keuzes = useMemo(() => {
    const alle = modelKeuzes(personas.data ?? [])
    return c ? keuzesVoorConcept(alle, c as unknown as AutoConcept) : alle
  }, [personas.data, c])
  if (info.error) {
    return <Modal title="Scheduled Mass Message" onClose={onSluit}><ErrorNote error={info.error} /></Modal>
  }
  if (!info.data || !personas.data || !c) return null
  if (info.data.locked) {
    return (
      <Modal title="Scheduled Mass Message" onClose={onSluit}>
        <p className="mut2" data-testid="gepland-vergrendeld">{info.data.lock_reason}</p>
      </Modal>
    )
  }
  return (
    <ConceptBewerken c={c} keuzes={keuzes} zone={zone} auto={info.data.auto} gepland
                     magDirect={magMassDm(mij.data, 'direct')} magNative={magMassDm(mij.data, 'native')}
                     magBetaald={magBetaaldeMassDm(mij.data)} onSluit={onSluit} onOpgeslagen={onOpgeslagen} />
  )
}
